import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Award, BookOpen, Star, StarHalf, Book, Home, X } from 'lucide-react';
import { asset } from '../../../shared/utils/asset';
import { SEO } from '../../../shared/components';
import { useSettingsContext } from '../../../shared/context/SettingsContext';
import { useGallery } from '../../../features/gallery/hooks/useGallery';

// Shared components - cards have white bg for contrast on cyan
const FeatureCard = ({ icon, title, description, className = "" }: any) => {
  const renderIcon = () => {
    if (React.isValidElement(icon)) {
      return React.cloneElement(icon as React.ReactElement<any>, { className: 'w-6 h-6 text-accent' });
    }
    return icon;
  };
  return (
    <div className={`p-6 bg-white rounded-xl shadow-lg ${className}`}>
      <div className="w-12 h-12 flex items-center justify-center rounded-full mb-4 bg-accent/10">{renderIcon()}</div>
      <h3 className="text-xl font-semibold mb-2 text-foreground">{title}</h3>
      <p className="text-muted-foreground">{description}</p>
    </div>
  );
};

const ProgramCard = ({ tag, title, description, link, className = "" }: any) => (
  <div className={`bg-white p-8 rounded-xl shadow-lg transform transition-all duration-300 hover:-translate-y-1 ${className}`}>
    <span className="text-sm font-medium text-muted-foreground mb-4 block">{tag}</span>
    <h3 className="text-2xl font-semibold text-foreground mb-3">{title}</h3>
    <p className="text-muted-foreground mb-6">{description}</p>
    <Link to={link} className="text-accent font-semibold inline-flex items-center group">
      Learn more <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
    </Link>
  </div>
);

const SuccessStory = ({ name, exam, percentage, testimonial, initial, className = "" }: any) => (
  <div className={`bg-white p-8 rounded-xl shadow-lg ${className}`}>
    <div className="flex items-start gap-4 mb-6">
      <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
        <span className="text-lg font-bold text-accent">{initial}</span>
      </div>
      <div>
        <h3 className="text-lg font-semibold text-foreground">{name}</h3>
        <p className="text-sm text-muted-foreground">{exam}</p>
      </div>
    </div>
    <span className="text-5xl font-bold text-foreground block mb-4">{percentage}</span>
    <p className="text-muted-foreground">"{testimonial}"</p>
  </div>
);

const TestimonialCard = ({ rating, text, name, role, initials, className = "" }: any) => (
  <div className={`bg-white p-8 rounded-xl shadow-lg ${className}`}>
    <div className="flex items-center mb-4">
      {[...Array(Math.floor(rating))].map((_, i) => <Star key={i} className="w-4 h-4 text-accent fill-accent" />)}
      {rating % 1 !== 0 && <StarHalf className="w-4 h-4 text-accent" />}
    </div>
    <p className="text-foreground mb-6">"{text}"</p>
    <div className="flex items-center">
      <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center mr-3">
        <span className="font-medium text-foreground">{initials}</span>
      </div>
      <div>
        <h4 className="font-semibold text-foreground">{name}</h4>
        <p className="text-sm text-muted-foreground">{role}</p>
      </div>
    </div>
  </div>
);

/**
 * Home Dark Blue Variant - All sections use accent/cyan color (bg-accent)
 * Route: /home-dark-blue
 */
const HomeDarkBlue: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { settings } = useSettingsContext();

  useEffect(() => {
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    document.body.classList.toggle('overflow-hidden', !isMenuOpen);
  };

  const features = [
    { icon: <Users />, title: 'Personalized Approach', description: "Individualized learning plans tailored to each student's needs." },
    { icon: <Award />, title: 'Expert Teachers', description: 'Qualified educators with extensive experience and passion.' },
    { icon: <BookOpen />, title: 'Multiple Curricula', description: 'CBSE, IGCSE, IB, Federal, and Bangladesh Board support.' },
  ];

  const programs = [
    { tag: 'Grades K.G-12', title: 'Core Subjects', description: 'Mathematics, Physics, Chemistry, Biology, English, and more.', link: '/services' },
    { tag: 'All Levels', title: 'Specialized Programs', description: 'Crash courses, entrance test prep, and foundation courses.', link: '/services' },
    { tag: 'Summer', title: 'Summer Courses', description: 'Keep the learning momentum going during summer.', link: '/services' },
  ];

  const successStories = [
    { name: "Lalenthika", exam: "CBSE Board Exam", percentage: "93.4%", testimonial: "Tuition Point helped me excel in my board exams.", initial: "L" },
    { name: "Keerthana", exam: "CBSE Board Exam", percentage: "93%", testimonial: "Personalized attention made all the difference.", initial: "K" }
  ];

  const testimonials = [
    { rating: 5, text: "Remarkable improvement in my daughter's academic skills over 3 months.", name: "Ponnu Menu", role: "Parent", initials: "PM" },
    { rating: 5, text: "Teaching methods have really helped my child grasp difficult concepts.", name: "Aatha Sharma", role: "Parent", initials: "AS" },
    { rating: 4.5, text: "Thanks to coaching, my kids gained admission to BPS.", name: "Kesavan Nagaraj", role: "Parent", initials: "KN" }
  ];

  const quickLinks = [
    { icon: <Book className="w-6 h-6 text-accent" />, title: "Curriculum", description: "Explore our curricula.", link: "/curriculum" },
    { icon: <Users className="w-6 h-6 text-accent" />, title: "Parent Support", description: "Comprehensive support systems.", link: "/parent-support" },
    { icon: <Home className="w-6 h-6 text-accent" />, title: "Our Campus", description: "Tour our facilities.", link: "/campus" }
  ];

  // All sections use the accent cyan color
  const bgMain = "bg-accent";

  return (
    <div className={`${bgMain} text-white min-h-screen`}>
      <SEO title="Home Dark Blue Variant | Tuition Point Qatar" />

      {/* Demo Label */}
      <div className="bg-yellow-400 text-gray-900 text-center py-2 text-sm font-medium">
        🎨 Variant: <strong>DARK BLUE</strong> - All sections use bg-accent (#06B6D4)
      </div>

      {/* Variant Navigation */}
      <div className="bg-white py-3 border-b">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex justify-center gap-3 flex-wrap">
            <Link to="/home-white" className="px-3 py-1.5 bg-white border border-gray-300 rounded font-medium text-sm text-gray-800 hover:bg-gray-100">White</Link>
            <Link to="/home-light-blue" className="px-3 py-1.5 bg-sky-100 border border-sky-300 rounded font-medium text-sm text-gray-800 hover:bg-sky-200">Light Blue</Link>
            <Link to="/home-blue" className="px-3 py-1.5 bg-cyan-200 border border-cyan-400 rounded font-medium text-sm text-gray-800 hover:bg-cyan-300">Blue</Link>
            <Link to="/home-dark-blue" className="px-3 py-1.5 bg-cyan-500 text-white border-2 border-gray-800 rounded font-medium text-sm">Dark Blue ●</Link>
            <Link to="/" className="px-3 py-1.5 bg-gray-800 text-white rounded font-medium text-sm hover:bg-gray-900">← Main</Link>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        id="mobile-menu"
        className={`fixed inset-0 z-50 bg-white transition-transform duration-300 ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="container mx-auto px-4 h-full flex flex-col py-8 max-w-7xl">
          <div className="flex justify-between items-center mb-12">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center text-white">
                <span className="font-display font-bold text-lg">TP</span>
              </div>
              <span className="font-display font-bold text-primary text-lg">Tuition Point</span>
            </Link>
            <button onClick={toggleMenu} aria-label="Close Menu">
              <X className="w-6 h-6 text-primary" />
            </button>
          </div>
          <nav className="flex flex-col space-y-6 text-xl">
            <Link to="/" onClick={toggleMenu} className="nav-link text-foreground hover:text-accent transition-colors">Home</Link>
            <Link to="/about" onClick={toggleMenu} className="nav-link text-foreground hover:text-accent transition-colors">About Us</Link>
            <Link to="/services" onClick={toggleMenu} className="nav-link text-foreground hover:text-accent transition-colors">Services</Link>
            <Link to="/team" onClick={toggleMenu} className="nav-link text-foreground hover:text-accent transition-colors">Our Team</Link>
            <Link to="/achievements" onClick={toggleMenu} className="nav-link text-foreground hover:text-accent transition-colors">Success Stories</Link>
            <Link to="/contact" onClick={toggleMenu} className="nav-link text-foreground hover:text-accent transition-colors">Contact</Link>
            <Link to="/enrollment" onClick={toggleMenu} className="btn-primary w-full text-center mt-4">Enroll Now</Link>
          </nav>
        </div>
      </div>

      {/* Announcement Bar - kept as original */}
      {(() => {
        const notifications = [
          settings?.notification1,
          settings?.notification2,
          settings?.notification3
        ].filter(Boolean);

        if (notifications.length === 0) return null;

        return (
          <div className="bg-yellow-400 py-3 overflow-hidden">
            <div className="animate-scroll whitespace-nowrap hover:pause-animation">
              {[...Array(3)].map((_, repeatIndex) => (
                <React.Fragment key={repeatIndex}>
                  {notifications.map((notification, index) => (
                    <React.Fragment key={`${repeatIndex}-${index}`}>
                      <span className="inline-block px-8 text-gray-900 font-semibold">
                        {notification}
                      </span>
                      {!(repeatIndex === 2 && index === notifications.length - 1) && (
                        <span className="inline-block px-2 text-gray-900">•</span>
                      )}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Hero Section */}
      <section className={`pt-16 pb-20 md:pt-20 md:pb-24 ${bgMain}`}>
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-medium text-white/70 mb-2 block">Licensed by the Ministry of Education & Higher Education</span>
              <h1 className="text-white text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
                Expert K-12 Tutoring in <br/>Al Wakra, Qatar
              </h1>
              <p className="text-lg text-white/90 mb-8 max-w-lg">
                Welcome to <span className="text-yellow-300 font-semibold">Tuition Point Education Center</span>. Personalized tutoring for CBSE, IGCSE, IB, Federal & Bangladesh Board students.
              </p>
              <div className="flex items-center gap-4">
                <Link to="/enrollment" className="bg-white hover:bg-gray-100 text-cyan-600 font-semibold py-3 px-6 rounded-lg shadow-md flex items-center">
                  Enroll Today <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
                <Link to="/contact" className="bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold py-3 px-6 rounded-lg shadow-md">
                  Contact Us
                </Link>
              </div>
            </div>
            <div>
              <img src={asset('Home/home.jpg')} alt="Students" className="w-full h-auto rounded-2xl shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Latest Updates & Poster Section - kept as original */}
      {(() => {
        const updates = [
          { title: settings?.update1Title, description: settings?.update1Description },
          { title: settings?.update2Title, description: settings?.update2Description },
          { title: settings?.update3Title, description: settings?.update3Description },
          { title: settings?.update4Title, description: settings?.update4Description },
          { title: settings?.update5Title, description: settings?.update5Description },
        ].filter(update => update.title);

        const hasAdvertisement = !!settings?.advertisementImageUrl;
        const hasUpdates = updates.length > 0;

        if (!hasUpdates && !hasAdvertisement) return null;

        return (
          <section className="py-16 bg-yellow-50">
            <div className="container mx-auto px-4 max-w-7xl">
              {hasAdvertisement ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                  {hasUpdates && (
                    <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
                      <h2 className="text-3xl font-bold text-foreground mb-6">Latest Updates</h2>
                      <div className="space-y-6">
                        {updates.map((update, index) => (
                          <div key={index} className="border-l-4 border-accent pl-4 py-2">
                            <h3 className="text-lg font-semibold text-foreground mb-2">{update.title}</h3>
                            <p className="text-foreground">{update.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="rounded-xl overflow-hidden shadow-lg">
                    <img
                      src={settings.advertisementImageUrl || ''}
                      alt="Tuition Point Advertisement"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
              ) : (
                hasUpdates && (
                  <div>
                    <h2 className="text-3xl font-bold text-foreground mb-8 text-center">Latest Updates</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {updates.map((update, index) => (
                        <div key={index} className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                          <div className="border-l-4 border-accent pl-4 py-2">
                            <h3 className="text-lg font-semibold text-foreground mb-2">{update.title}</h3>
                            <p className="text-foreground">{update.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>
          </section>
        );
      })()}

      {/* Features Section */}
      <section className={`py-24 ${bgMain}`}>
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Why Choose Tuition Point Qatar?</h2>
            <p className="text-lg text-white/80">Al Wakra's trusted education center.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((f, i) => <FeatureCard key={i} {...f} />)}
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className={`py-24 ${bgMain}`}>
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl font-semibold text-white mb-4">Our Programs</h2>
            <p className="text-lg text-white/80">Comprehensive academic support.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {programs.map((p, i) => <ProgramCard key={i} {...p} />)}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className={`py-24 ${bgMain}`}>
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl font-semibold text-white mb-4">Success Stories</h2>
            <p className="text-lg text-white/80">Remarkable results through dedication.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {successStories.map((s, i) => <SuccessStory key={i} {...s} />)}
          </div>
          <div className="text-center mt-16">
            <Link
              to="/achievements"
              className="bg-white hover:bg-gray-100 text-cyan-600 font-semibold py-3 px-6 rounded-lg shadow-md transition duration-200"
            >
              View All Success Stories
            </Link>
          </div>
        </div>
      </section>

      {/* Gallery Section - kept as original */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16 animate-slide-up">
            <h2 className="text-4xl font-display font-semibold text-foreground mb-4">Our Gallery</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Explore moments from our vibrant learning community - from classroom achievements to special events.
            </p>
          </div>

          {(() => {
            const { galleryItems, loading, error } = useGallery(false);
            const recentItems = galleryItems.slice(0, 4);

            if (loading) {
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="animate-pulse">
                      <div className="bg-gray-200 w-full h-64 rounded-xl"></div>
                    </div>
                  ))}
                </div>
              );
            }

            if (error || recentItems.length === 0) {
              return (
                <div className="text-center py-12">
                  <div className="bg-gray-100 rounded-xl p-12 max-w-md mx-auto">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Gallery Coming Soon</h3>
                    <p className="text-gray-500">We're preparing amazing photos and videos!</p>
                  </div>
                </div>
              );
            }

            return (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                  {recentItems.map((item, index) => (
                    <div key={item.id} className={`relative group overflow-hidden rounded-xl shadow-lg animate-slide-up animation-delay-${index + 1}`}>
                      {item.fileType === 'image' ? (
                        <img
                          src={item.url}
                          alt={item.title}
                          className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      ) : (
                        <div className="relative w-full h-64 bg-gray-900">
                          <video src={item.url} className="w-full h-full object-cover" muted preload="metadata" />
                          <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                            <div className="p-3 bg-white/20 rounded-full">
                              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z"/>
                              </svg>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute bottom-4 left-4 right-4">
                          <h3 className="text-white font-semibold text-sm">{item.title}</h3>
                          <span className="text-white/60 text-xs">{item.fileType.toUpperCase()}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="text-center">
                  <Link
                    to="/gallery"
                    className="bg-cyan-500 hover:bg-cyan-600 text-white font-semibold py-3 px-8 rounded-lg shadow-md transition duration-300 inline-flex items-center group"
                  >
                    View More Gallery
                    <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </>
            );
          })()}
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-24 ${bgMain}`}>
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">Ready to Start Your Educational Journey?</h2>
            <p className="text-lg mb-10 text-white/90">Join Tuition Point Education Center today and take the first step toward academic excellence.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/enrollment"
                className="bg-white text-cyan-600 hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg shadow-xl transition duration-300 text-lg"
              >
                Enroll Today
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white hover:bg-white hover:text-cyan-600 font-semibold py-3 px-8 rounded-lg transition duration-300 text-lg"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className={`py-24 ${bgMain}`}>
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <div className="flex items-center justify-center mb-6">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 text-yellow-300 fill-yellow-300" />)}
              <span className="ml-2 text-lg font-medium text-white/80">4.9/5</span>
            </div>
            <h2 className="text-4xl font-semibold text-white mb-4">What Parents Say</h2>
            <p className="text-lg text-white/80">Based on 42 reviews from our parent community.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((t, i) => <TestimonialCard key={i} {...t} />)}
          </div>
          <div className="text-center mt-16">
            <Link to="/reviews" className="text-white text-base font-semibold border-b-2 border-transparent hover:border-white transition duration-200 uppercase tracking-wider">
              View All Reviews
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className={`py-24 ${bgMain}`}>
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl font-semibold text-white mb-4">Explore More</h2>
            <p className="text-lg text-white/80">Discover all that Tuition Point Education Center has to offer.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {quickLinks.map((link, i) => (
              <Link to={link.link} key={i} className="bg-white p-8 rounded-xl shadow-lg flex flex-col items-start hover:-translate-y-1 transition-transform">
                <div className="w-14 h-14 bg-accent/10 rounded-xl flex items-center justify-center mb-6">{link.icon}</div>
                <h3 className="text-xl font-semibold mb-3 text-foreground">{link.title}</h3>
                <p className="text-muted-foreground">{link.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomeDarkBlue;
